import { Button } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { useState, useEffect, useMemo } from 'react'
import { SCREEN_SIZE } from '../constants/common'
import dayjs from 'dayjs'
import useIsMobile from '../hooks/useIsMobile'

const CalendarView = ({ events = [], onEventClick, currentMonth: externalCurrentMonth, onMonthChange }) => {
  const [internalCurrentMonth, setInternalCurrentMonth] = useState(externalCurrentMonth || dayjs())
  
  // Use external month if provided, otherwise use internal state
  const currentMonth = externalCurrentMonth || internalCurrentMonth
  
  // Sync internal state with external if provided
  useEffect(() => {
    if (externalCurrentMonth) {
      setInternalCurrentMonth(externalCurrentMonth)
    }
  }, [externalCurrentMonth])
  const isMobile = useIsMobile(SCREEN_SIZE.MOBILE)

  // Group events by date
  const eventsByDate = useMemo(() => {
    if (!events || events.length === 0) return {}
    
    const grouped = {}
    events.forEach(event => {
      // Handle different date field formats
      let dateKey = null
      if (event.date_time_start) {
        // Extract date from ISO datetime string
        dateKey = dayjs(event.date_time_start).format('YYYY-MM-DD')
      } else if (event.date) {
        dateKey = typeof event.date === 'string' ? event.date : dayjs(event.date).format('YYYY-MM-DD')
      } else if (event.startDate) {
        dateKey = typeof event.startDate === 'string' ? event.startDate : dayjs(event.startDate).format('YYYY-MM-DD')
      }
      
      if (dateKey) {
        if (!grouped[dateKey]) {
          grouped[dateKey] = []
        }
        grouped[dateKey].push(event)
      }
    })
    
    // Sort events by start_time within each date
    Object.keys(grouped).forEach(dateKey => {
      grouped[dateKey].sort((a, b) => {
        let timeA = '00:00'
        let timeB = '00:00'
        
        if (a.date_time_start) {
          timeA = dayjs(a.date_time_start).format('HH:mm')
        } else if (a.start_time) {
          timeA = a.start_time
        } else if (a.startTime) {
          timeA = a.startTime
        }
        
        if (b.date_time_start) {
          timeB = dayjs(b.date_time_start).format('HH:mm')
        } else if (b.start_time) {
          timeB = b.start_time
        } else if (b.startTime) {
          timeB = b.startTime
        }
        
        return timeA.localeCompare(timeB)
      })
    })
    
    return grouped
  }, [events])

  // Group events by time for same-day display
  const groupEventsByTime = (events) => {
    const grouped = {}
    events.forEach(event => {
      let time = '00:00'
      
      if (event.date_time_start) {
        // Extract time from ISO datetime string
        time = dayjs(event.date_time_start).format('HH:mm')
      } else if (event.start_time) {
        time = event.start_time
      } else if (event.startTime) {
        time = event.startTime
      }
      
      if (!grouped[time]) {
        grouped[time] = []
      }
      grouped[time].push(event)
    })
    return grouped
  }

  // Get rolling 7 days starting from the current anchor date
  const getCalendarDays = () => {
    const days = []
    for (let i = 0; i < 7; i++) {
      days.push(currentMonth.clone().add(i, 'day'))
    }
    return days
  }

  const calendarDays = getCalendarDays()

  const goToPreviousMonth = () => {
    const newStart = currentMonth.subtract(7, 'day')
    setInternalCurrentMonth(newStart)
    if (onMonthChange) onMonthChange(newStart)
  }

  const goToNextMonth = () => {
    const newStart = currentMonth.add(7, 'day')
    setInternalCurrentMonth(newStart)
    if (onMonthChange) onMonthChange(newStart)
  }

  const goToToday = () => {
    const today = dayjs()
    setInternalCurrentMonth(today)
    if (onMonthChange) {
      onMonthChange(today)
    }
  }

  const isToday = (day) => {
    return day.isSame(dayjs(), 'day')
  }

  // Get background color based on appointment type
  const getAppointmentTypeColor = (appointmentType) => {
    const type = appointmentType || ''
    const colorMap = {
      'discovery-inspiration': '#1890ff', 
      'free shirt': '#52c41a', 
      'new order': '#faad14', 
      'fitting': '#722ed1', 
      'adjustment': '#eb2f96', 
      'partner': '#fa541c', 
      'other': '#f17a80', 
    }
    return colorMap[type.toLowerCase()] || '#1890ff' 
  }

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <Button icon={<LeftOutlined />} onClick={goToPreviousMonth} />
        <h2 className="calendar-month-title">
          {`${currentMonth.format('MMM D')} - ${currentMonth.clone().add(6, 'day').format('MMM D, YYYY')}`}
        </h2>
        <Button icon={<RightOutlined />} onClick={goToNextMonth} />
        <Button onClick={goToToday} style={{ marginLeft: 16 }}>
          Today
        </Button>
      </div>
      <div className={`calendar-grid ${isMobile ? 'mobile' : 'desktop'}`}>
        <div className="calendar-weekdays">
          {calendarDays.map((day) => (
            <div key={day.format('YYYY-MM-DD')} className={`calendar-weekday ${isToday(day) ? 'today' : ''}`}>
              {isMobile ? (
                day.format('ddd')
              ) : (
                <>
                  <span className="calendar-weekday-name">{day.format('ddd')}</span>
                  <span className="calendar-weekday-date">{day.format('D')}</span>
                </>
              )}
            </div>
          ))}
        </div>
        
        {/* Calendar days */}
        <div className="calendar-days">
          {calendarDays.map((day, index) => {
            const dateKey = day.format('YYYY-MM-DD')
            const dayEvents = eventsByDate[dateKey] || []
            const eventsByTime = groupEventsByTime(dayEvents)
            const timeSlots = Object.keys(eventsByTime).sort()
            
            return (
              <div
                key={index}
                className={`calendar-day ${isToday(day) ? 'today' : ''}`}
              >
                {isMobile && (
                  <div className="calendar-day-number">
                    {day.format('D')}
                  </div>
                )}
                <div className="calendar-day-events">
                  {timeSlots.map(time => {
                    const eventsAtTime = eventsByTime[time]
                    return (
                      <div key={time} className="calendar-time-slot">
                        {eventsAtTime.map((event, eventIndex) => {
                          const appointmentType = event.appointment_type || event.appointmentType || ''
                          const backgroundColor = getAppointmentTypeColor(appointmentType)
                          
                          let timeRange = time
                          if (event.date_time_start && event.date_time_end) {
                            const startTime = dayjs(event.date_time_start).format('HH:mm')
                            const endTime = dayjs(event.date_time_end).format('HH:mm')
                            timeRange = `${startTime} – ${endTime}`
                          } else if (event.date_time_start) {
                            timeRange = dayjs(event.date_time_start).format('HH:mm')
                          }
                          
                          return (
                          <div
                            key={event.id || eventIndex}
                            className="calendar-event"
                            style={{ backgroundColor, cursor: onEventClick ? 'pointer' : 'default' }}
                            onClick={() => onEventClick && onEventClick(event)}
                            title={`${event.client_name || event.clientName || 'Event'} - ${timeRange} - ${appointmentType} - ${event.related_sales?.name?.en || event.related_sales?.name?.zh || event.related_sales?.name || event.related_sales?.email || ''}`}
                          >
                            <div className="calendar-event-content">
                              <div className="calendar-event-client">
                                {event.client_name || event.clientName || 'Event'}
                              </div>
                              <div className="calendar-event-time-range">{timeRange}</div>
                              <div className="calendar-event-details">
                                <span className="calendar-event-type">
                                  {event.appointment_type || event.appointmentType || ''}
                                </span>
                                <span className="calendar-event-stylist">
                                  {event.related_sales?.name?.en || event.related_sales?.name?.zh || event.related_sales?.name || event.related_sales?.email || ''}
                                </span>
                              </div>
                            </div>
                          </div>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default CalendarView

